import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, ImageUp, Loader2, Tag, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@satyrsmc/shared/components";
import type { MediaContactTag } from "@satyrsmc/shared/dto/mediaContactTag";
import { trpc } from "@/trpc";
import { PhotoTagEditor, type QueuedPhotoTag } from "./PhotoTagEditor";

/** Client-side mirror of the server's limits, so obvious rejects never leave the browser. */
const MAX_FILES_PER_BATCH = 20;
const MAX_FILE_BYTES = 25 * 1024 * 1024;
// HEIC is listed by extension as well: browsers that can't decode the format
// don't recognise its mime type either, and would filter the file out.
const ACCEPTED_TYPES = "image/jpeg,image/png,.heic,.heif";

type QueueStatus = "queued" | "uploading" | "done" | "failed" | "rejected";

interface QueueItem {
  id: string;
  file: File;
  status: QueueStatus;
  /** Object URL for the row's thumbnail preview; revoked when the section unmounts. */
  previewUrl: string;
  /** Why the photo was rejected or failed — shown next to the filename. */
  reason?: string;
}

/** One successfully finalized upload, as the review step edits it. */
interface UploadedPhoto {
  mediaId: string;
  /** The client's original filename, for labels. */
  fileName: string;
  /** The server's canonical filename — what the photo is called from here on. */
  serverFilename: string;
  /** Server-signed URL for the web derivative — displayable even for HEIC. */
  previewUrl: string;
  /** The caption that rode the finalize call, pre-filling the editor. */
  initialDescription: string | null;
}

const STATUS_LABELS: Record<QueueStatus, string> = {
  queued: "Queued",
  uploading: "Uploading…",
  done: "Uploaded",
  failed: "Failed",
  rejected: "Not accepted",
};

/**
 * Photo submission wizard for a gallery's event, in two steps. Step 1 is the
 * upload queue: file picker, per-file caption inputs, sequential uploads with
 * per-file retry. Step 2 is the review step, entered once the queue drains
 * with at least one success: each uploaded photo is shown from its SERVER
 * preview (browsers can't decode HEIC locally, so tagging had to move here,
 * after the server has cut a JPEG derivative), with a description editor and
 * a tag editor whose edits save immediately through their own mutations. A
 * Finish button concludes the submission — that is what sends the one batch
 * email to the admins. Rendered only for eligible contributors (the gallery
 * payload's `can_contribute`); uploads land as pending review either way.
 */
export function PhotoUploadSection({ eventId }: { eventId: string }) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [step, setStep] = useState<"queue" | "review" | "done">("queue");
  const [uploaded, setUploaded] = useState<UploadedPhoto[]>([]);
  /** Tags per media id — the server's rows are the source of truth. */
  const [photoTags, setPhotoTags] = useState<ReadonlyMap<string, MediaContactTag[]>>(
    () => new Map(),
  );
  /** The uploaded photo whose tag editor is open. */
  const [tagEditorMediaId, setTagEditorMediaId] = useState<string | null>(null);
  const [isFinishing, setIsFinishing] = useState(false);
  const requestUpload = trpc.members.gallery.requestUpload.useMutation();
  const finalizeUpload = trpc.members.gallery.finalizeUpload.useMutation();
  const completeSubmission = trpc.members.gallery.completeSubmission.useMutation();
  const addOwnPhotoTag = trpc.members.gallery.addOwnPhotoTag.useMutation();
  const removeOwnPhotoTag = trpc.members.gallery.removeOwnPhotoTag.useMutation();
  const updateOwnPhotoDescription = trpc.members.gallery.updateOwnPhotoDescription.useMutation();
  const utils = trpc.useUtils();

  // Refs, not state: the upload loop reads these between awaits, where a
  // re-rendered state value would still be the one captured at loop start.
  const runningRef = useRef(false);
  const successCountRef = useRef(0);
  const notifiedRef = useRef(false);
  /** Every object URL this section minted, revoked together on unmount. */
  const previewUrlsRef = useRef<string[]>([]);
  /** Captions by queue-item id, read at upload time (state would go stale mid-batch). */
  const descriptionsRef = useRef<Map<string, string>>(new Map());

  // If the member closes the page from the review step without hitting
  // Finish, the photos are already recorded as pending — nothing is lost.
  // Only the one batch notification email to the admins is skipped, a
  // deliberate tradeoff: better a quiet submission than emailing on every
  // half-finished wizard.
  useEffect(
    () => () => {
      for (const url of previewUrlsRef.current) URL.revokeObjectURL(url);
    },
    [],
  );

  const setStatus = useCallback((id: string, status: QueueStatus, reason?: string) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status, reason } : i)));
  }, []);

  /**
   * Three legs per file: ask the server for a presigned PUT, send the bytes
   * STRAIGHT to the bucket (no cookies — the URL is the credential, and this
   * bypasses the ~8MiB proxy body ceiling between Netlify and the API), then
   * have the server validate what landed and record the pending submission.
   * The finalize response carries the server preview the review step shows.
   */
  const uploadOne = useCallback(
    async (item: QueueItem): Promise<UploadedPhoto> => {
      const { file } = item;
      const ticket = await requestUpload.mutateAsync({
        event_id: eventId,
        filename: file.name,
        mime_type: file.type,
        byte_size: file.size,
      });
      const res = await fetch(ticket.upload_url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": ticket.content_type },
      });
      if (!res.ok) throw new Error("Upload failed. Please try again.");
      // The caption is read at send time, not queue time — the uploader may
      // still be typing it while earlier files in the batch upload.
      const description = descriptionsRef.current.get(item.id)?.trim() || null;
      const result = await finalizeUpload.mutateAsync({
        event_id: eventId,
        media_id: ticket.media_id,
        filename: file.name,
        mime_type: file.type,
        description,
      });
      return {
        mediaId: result.id,
        fileName: file.name,
        serverFilename: result.filename,
        previewUrl: result.preview_url,
        initialDescription: description,
      };
    },
    [eventId, requestUpload, finalizeUpload],
  );

  /**
   * Uploads a batch one request at a time. When the queue drains with at
   * least one success the wizard moves to the review step; the admins are not
   * notified until the member concludes it there with Finish.
   */
  const runQueue = useCallback(
    async (batch: QueueItem[]) => {
      if (runningRef.current) return;
      runningRef.current = true;
      setIsUploading(true);

      let batchOk = 0;
      let batchFailed = 0;
      for (const item of batch) {
        setStatus(item.id, "uploading");
        try {
          const photo = await uploadOne(item);
          successCountRef.current += 1;
          batchOk += 1;
          setUploaded((prev) => [...prev, photo]);
          setStatus(item.id, "done");
        } catch (err) {
          batchFailed += 1;
          setStatus(item.id, "failed", err instanceof Error ? err.message : "Upload failed");
        }
      }

      runningRef.current = false;
      setIsUploading(false);

      if (batchFailed === 0) {
        toast.success(
          `${batchOk === 1 ? "1 photo" : `${batchOk} photos`} uploaded — an admin will review before they appear in the gallery.`,
        );
      } else if (batchOk > 0) {
        toast.error(
          `${batchOk} uploaded, ${batchFailed} failed — retry the failed photos below. An admin will review the uploaded ones.`,
        );
      } else {
        toast.error("Uploads failed — nothing was submitted. Retry the photos below.");
      }

      // At least one photo made it: move to the review step, where captions
      // and tags are edited against the server previews. Failed files ride
      // along with their reasons (and a retry). With zero successes there is
      // nothing to review, so the queue stays put for retries.
      if (successCountRef.current > 0) {
        setStep("review");
      }
    },
    [setStatus, uploadOne],
  );

  /**
   * Concludes the submission: one admin notification for the whole batch —
   * retries included — then the thanks state. Guarded so a double click can
   * never send a second email.
   */
  const handleFinish = useCallback(async () => {
    if (notifiedRef.current || successCountRef.current === 0) return;
    notifiedRef.current = true;
    setIsFinishing(true);
    try {
      await completeSubmission.mutateAsync({
        eventId,
        uploadedCount: successCountRef.current,
      });
      void utils.members.gallery.invalidate();
      setStep("done");
    } catch {
      notifiedRef.current = false;
      toast.error("Something went wrong finishing the submission. Please try again.");
    } finally {
      setIsFinishing(false);
    }
  }, [completeSubmission, eventId, utils]);

  const handleFilesSelected = useCallback((fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const selected = Array.from(fileList);

    let accepted = 0;
    const next: QueueItem[] = [];
    for (const file of selected) {
      const id = `${file.name}-${crypto.randomUUID()}`;
      const previewUrl = URL.createObjectURL(file);
      previewUrlsRef.current.push(previewUrl);
      if (file.size > MAX_FILE_BYTES) {
        next.push({
          id,
          file,
          status: "rejected",
          previewUrl,
          reason: "Larger than the 25MB limit",
        });
        continue;
      }
      if (accepted >= MAX_FILES_PER_BATCH) {
        next.push({
          id,
          file,
          status: "rejected",
          previewUrl,
          reason: `Only ${MAX_FILES_PER_BATCH} photos can be submitted at a time`,
        });
        continue;
      }
      accepted += 1;
      next.push({ id, file, status: "queued", previewUrl });
    }

    setItems((prev) => [...prev, ...next]);
  }, []);

  const queuedItems = items.filter((item) => item.status === "queued");
  /** Review-step stragglers: anything not uploaded — failures, client rejects, and retries in flight. */
  const unfinishedItems = items.filter((item) => item.status !== "done");

  const handleAddTag = useCallback(
    async (mediaId: string, tag: QueuedPhotoTag) => {
      try {
        const created = await addOwnPhotoTag.mutateAsync({
          media_id: mediaId,
          contact_id: tag.contact_id,
          x: tag.x,
          y: tag.y,
          w: tag.w,
          h: tag.h,
        });
        setPhotoTags((prev) => {
          const next = new Map(prev);
          next.set(mediaId, [...(prev.get(mediaId) ?? []), created]);
          return next;
        });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to add the tag");
      }
    },
    [addOwnPhotoTag],
  );

  const handleRemoveTag = useCallback(
    async (mediaId: string, contactId: string) => {
      const tag = (photoTags.get(mediaId) ?? []).find((t) => t.contact_id === contactId);
      if (!tag) return;
      try {
        await removeOwnPhotoTag.mutateAsync({ tag_id: tag.id });
        setPhotoTags((prev) => {
          const next = new Map(prev);
          next.set(
            mediaId,
            (prev.get(mediaId) ?? []).filter((t) => t.id !== tag.id),
          );
          return next;
        });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to remove the tag");
      }
    },
    [photoTags, removeOwnPhotoTag],
  );

  const handleSaveDescription = useCallback(
    async (mediaId: string, description: string | null) => {
      try {
        await updateOwnPhotoDescription.mutateAsync({ media_id: mediaId, description });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to save the description");
      }
    },
    [updateOwnPhotoDescription],
  );

  const handleRetry = useCallback(
    (item: QueueItem) => {
      setStatus(item.id, "queued");
      void runQueue([item]);
    },
    [runQueue, setStatus],
  );

  const tagEditorPhoto = tagEditorMediaId
    ? (uploaded.find((photo) => photo.mediaId === tagEditorMediaId) ?? null)
    : null;

  if (step === "done") {
    return (
      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="size-5 shrink-0 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Photos submitted</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          {uploaded.length === 1 ? "Your photo is" : `Your ${uploaded.length} photos are`} awaiting
          review — an admin will look them over before they appear in the gallery.
        </p>
      </section>
    );
  }

  if (step === "review") {
    return (
      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">Review Your Photos</h2>
          <p className="text-sm text-muted-foreground">
            Add captions and tag people, then hit Finish. An admin reviews everything before it
            appears in the gallery.
          </p>
        </div>

        <ul className="space-y-1">
          {uploaded.map((photo) => (
            <li
              key={photo.mediaId}
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
            >
              <span className="size-20 shrink-0 overflow-hidden rounded bg-muted">
                {/* The SERVER preview — a JPEG derivative, so it renders even
                    when the original was a HEIC the browser can't decode. */}
                <img src={photo.previewUrl} alt="" className="size-full object-cover" />
              </span>
              <span className="min-w-0 flex-1 space-y-1">
                {/* The canonical server name is the photo's identity from here
                    on; the original stays visible so the uploader can confirm
                    which file this was. */}
                <span className="block truncate">{photo.serverFilename}</span>
                {photo.fileName !== photo.serverFilename && (
                  <span
                    className="block truncate text-xs text-muted-foreground"
                    title={photo.fileName}
                  >
                    was {photo.fileName}
                  </span>
                )}
                <ReviewDescriptionInput photo={photo} onSave={handleSaveDescription} />
                <button
                  type="button"
                  aria-label={`Tag people in ${photo.fileName}`}
                  className="flex items-center gap-1 text-xs text-yellow-700 hover:underline dark:text-yellow-400"
                  onClick={() => setTagEditorMediaId(photo.mediaId)}
                >
                  <Tag className="size-3.5" />
                  {(photoTags.get(photo.mediaId)?.length ?? 0) > 0
                    ? `Edit tags (${photoTags.get(photo.mediaId)?.length})`
                    : "Add tags"}
                </button>
              </span>
            </li>
          ))}
          {unfinishedItems.map((item) => (
            <li
              key={item.id}
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
            >
              {item.status === "uploading" || item.status === "queued" ? (
                <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
              ) : (
                <TriangleAlert className="size-4 shrink-0 text-destructive" />
              )}
              <span className="min-w-0 flex-1 truncate">{item.file.name}</span>
              <span className="shrink-0 text-muted-foreground">{STATUS_LABELS[item.status]}</span>
              {item.reason && (
                <span className="shrink-0 text-xs text-destructive">{item.reason}</span>
              )}
              {item.status === "failed" && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isUploading}
                  onClick={() => handleRetry(item)}
                >
                  Retry
                </Button>
              )}
            </li>
          ))}
        </ul>

        <Button
          className="w-full"
          disabled={isFinishing || isUploading}
          onClick={() => void handleFinish()}
        >
          Finish
        </Button>

        {tagEditorPhoto && (
          <PhotoTagEditor
            eventId={eventId}
            photoName={tagEditorPhoto.fileName}
            previewUrl={tagEditorPhoto.previewUrl}
            tags={(photoTags.get(tagEditorPhoto.mediaId) ?? []).map((tag) => ({
              contact_id: tag.contact_id,
              display_name: tag.contact_display_name,
              x: tag.x,
              y: tag.y,
              w: tag.w,
              h: tag.h,
            }))}
            onAddTag={(tag) => void handleAddTag(tagEditorPhoto.mediaId, tag)}
            onRemoveTag={(contactId) => void handleRemoveTag(tagEditorPhoto.mediaId, contactId)}
            onClose={() => setTagEditorMediaId(null)}
          />
        )}
      </section>
    );
  }

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">Submit Photos</h2>
        <p className="text-sm text-muted-foreground">
          JPEG, PNG, or HEIC — up to {MAX_FILES_PER_BATCH} photos at a time, 25MB each. Add optional
          captions, then hit Upload. You can tag people after the upload finishes.
        </p>
      </div>

      <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground hover:bg-muted/50">
        <ImageUp className="size-5 shrink-0" />
        <span>Choose photos</span>
        <input
          type="file"
          multiple
          accept={ACCEPTED_TYPES}
          className="sr-only"
          onChange={(e) => {
            handleFilesSelected(e.target.files);
            e.target.value = "";
          }}
        />
      </label>

      {items.length > 0 && (
        <ul className="max-h-64 space-y-1 overflow-y-auto">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
            >
              <span className="size-20 shrink-0 overflow-hidden rounded bg-muted">
                {/* HEIC previews can't be decoded by most browsers — hide the
                    broken image and leave the neutral square. The server
                    preview in the review step covers them. */}
                <img
                  src={item.previewUrl}
                  alt=""
                  className="size-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </span>
              {item.status === "uploading" && (
                <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
              )}
              {item.status === "done" && (
                <CheckCircle2 className="size-4 shrink-0 text-muted-foreground" />
              )}
              {(item.status === "failed" || item.status === "rejected") && (
                <TriangleAlert className="size-4 shrink-0 text-destructive" />
              )}
              <span className="min-w-0 flex-1 space-y-1">
                <span className="block truncate">{item.file.name}</span>
                {item.status === "queued" ? (
                  <input
                    type="text"
                    aria-label={`Caption for ${item.file.name}`}
                    placeholder="Add a caption (optional)"
                    maxLength={2000}
                    className="w-full rounded border border-border bg-background px-2 py-1 text-xs"
                    onChange={(e) => descriptionsRef.current.set(item.id, e.target.value)}
                  />
                ) : (
                  descriptionsRef.current.get(item.id)?.trim() && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {descriptionsRef.current.get(item.id)}
                    </span>
                  )
                )}
              </span>
              <span className="shrink-0 text-muted-foreground">{STATUS_LABELS[item.status]}</span>
              {item.reason && (
                <span className="shrink-0 text-xs text-destructive">{item.reason}</span>
              )}
              {item.status === "failed" && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isUploading}
                  onClick={() => handleRetry(item)}
                >
                  Retry
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {queuedItems.length > 0 && (
        <Button
          className="w-full"
          disabled={isUploading}
          onClick={() => void runQueue(queuedItems)}
        >
          {queuedItems.length === 1 ? "Upload 1 photo" : `Upload ${queuedItems.length} photos`}
        </Button>
      )}
    </section>
  );
}

/**
 * The review step's description editor for one uploaded photo: pre-filled
 * with the caption that rode the upload, saved on blur when it changed. The
 * save sends `trimmed || null`, matching the semantics captions had at
 * finalize time.
 */
function ReviewDescriptionInput({
  photo,
  onSave,
}: {
  photo: UploadedPhoto;
  onSave: (mediaId: string, description: string | null) => Promise<void>;
}) {
  const [value, setValue] = useState(photo.initialDescription ?? "");
  /** The last value the server has, normalized — a no-change blur sends nothing. */
  const savedRef = useRef<string | null>(photo.initialDescription);

  const handleBlur = () => {
    const normalized = value.trim() || null;
    if (normalized === savedRef.current) return;
    savedRef.current = normalized;
    void onSave(photo.mediaId, normalized);
  };

  return (
    <input
      type="text"
      aria-label={`Description for ${photo.fileName}`}
      placeholder="Add a caption (optional)"
      maxLength={2000}
      value={value}
      className="w-full rounded border border-border bg-background px-2 py-1 text-xs"
      onChange={(e) => setValue(e.target.value)}
      onBlur={handleBlur}
    />
  );
}
